/**
 * Geographic Risk Map Component
 * Interactive map showing member risk distribution, hotspots, and SDOH factors
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  MapPin,
  AlertTriangle,
  Users,
  Layers,
  Download,
  RefreshCw,
  Filter,
  X,
  Search,
  Car,
  Cloud,
  Home,
  Thermometer,
  Droplets,
  Flame,
  TrendingUp,
  Navigation,
  ZoomIn,
  ZoomOut,
  Locate,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';

interface MemberRisk {
  member_id: number;
  member_name: string;
  latitude: number;
  longitude: number;
  risk_level: 'healthy' | 'watch' | 'at_risk' | 'critical' | 'lost';
  churn_probability: number;
  engagement_score: number;
  zip_code: string;
}

interface Hotspot {
  id: string;
  center_lat: number;
  center_lon: number;
  radius_miles: number;
  member_count: number;
  avg_risk_score: number;
  risk_factors: string[];
  zip_code?: string;
  sdoh_data?: {
    transportation_barrier?: number;
    climate_risk?: number;
    housing_risk?: number;
    combined_risk?: number;
  };
}

interface GeographicRiskData {
  members: MemberRisk[];
  hotspots: Hotspot[];
  sdoh_layers: {
    poverty_rate: Record<string, number>;
    transportation_score: Record<string, number>;
    housing_risk: Record<string, number>;
  };
}

interface ZipCodeSDOHData {
  zip_code: string;
  data_seeded?: boolean;
  transportation?: {
    walk_score?: number;
    walk_score_description?: string;
    transit_score?: number;
    transit_score_description?: string;
    bike_score?: number;
    pct_public_transit?: number;
    mean_commute_time?: number;
    pct_no_vehicle?: number;
    access_score?: number;
    barrier_score?: number;
  };
  climate?: {
    climate_risk_score?: number;
    aqi_annual_median?: number;
    aqi_days_good?: number;
    aqi_days_unhealthy?: number;
    primary_pollutant?: string;
    flood_zone?: string;
    flood_risk_level?: string;
    wildfire_risk_level?: string;
    wildfire_risk_score?: number;
    environmental_health_score?: number;
  };
  housing?: {
    eviction_rate?: number;
    eviction_filing_rate?: number;
    pct_cost_burdened?: number;
    pct_severely_cost_burdened?: number;
    median_gross_rent?: number;
    pct_owner_occupied?: number;
    homeless_per_10k?: number;
    stability_score?: number;
    affordability_score?: number;
    risk_score?: number;
  };
  area?: {
    poverty_rate?: number;
    unemployment_rate?: number;
    uninsured_rate?: number;
    food_desert_flag?: boolean;
    pcp_density?: number;
    sdoh_composite_score?: number;
  };
  combined_sdoh_risk_score?: number;
}

interface GeographicRiskMapProps {
  cboId: number;
  onMemberClick?: (memberId: number) => void;
}

export const GeographicRiskMap: React.FC<GeographicRiskMapProps> = ({
  cboId,
  onMemberClick,
}) => {
  const { token: contextToken } = useAuth();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const [data, setData] = useState<GeographicRiskData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLayer, setSelectedLayer] = useState<string>('risk');
  const [showHotspots, setShowHotspots] = useState(true);
  const [showSDOH, setShowSDOH] = useState(false);
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [mapLoaded, setMapLoaded] = useState(false);

  // SDOH ZIP Code lookup state
  const [zipCodeSearch, setZipCodeSearch] = useState('');
  const [zipCodeSDOH, setZipCodeSDOH] = useState<ZipCodeSDOHData | null>(null);
  const [loadingSDOH, setLoadingSDOH] = useState(false);
  const [sdohError, setSDOHError] = useState<string | null>(null);

  // Location search state
  const [locationSearch, setLocationSearch] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const locationMarkerRef = useRef<any>(null);
  const [showSDOHPanel, setShowSDOHPanel] = useState(false);
  const sdohMarkerRef = useRef<any>(null);
  const sdohInfoWindowRef = useRef<any>(null);

  // Current location state
  const [currentLocationLoading, setCurrentLocationLoading] = useState(false);
  const [currentLocationError, setCurrentLocationError] = useState<string | null>(null);
  const currentLocationMarkerRef = useRef<any>(null);

  // Geocoding state
  const [geocodingInProgress, setGeocodingInProgress] = useState(false);
  const [geocodingResult, setGeocodingResult] = useState<{
    success: boolean;
    message: string;
    geocoded_count?: number;
    failed_count?: number;
  } | null>(null);

  // Helper function to get auth token
  const getAuthToken = (): string | null => {
    if (typeof window === 'undefined') return null;
    return contextToken || localStorage.getItem('auth_token');
  };

  useEffect(() => {
    const authToken = getAuthToken();
    if (cboId && authToken) {
      fetchGeographicRisk();
    } else if (!authToken) {
      setError('Authentication required');
      setLoading(false);
    }
  }, [cboId, contextToken]);

  useEffect(() => {
    console.log('[GeographicRiskMap] Map init effect:', {
      mapLoaded,
      loading,
      hasMapInstance: !!mapInstanceRef.current,
      hasMapRef: !!mapRef.current,
      hasData: !!data,
      memberCount: data?.members?.length || 0
    });
    // Only try to init map when: maps loaded, not already initialized, ref available (not loading), and we have data
    if (mapLoaded && !mapInstanceRef.current && mapRef.current && !loading && data) {
      console.log('[GeographicRiskMap] All conditions met, initializing map');
      initializeMap();
    }
  }, [mapLoaded, data, loading]); // Added data and loading dependencies

  useEffect(() => {
    console.log('[GeographicRiskMap] Marker update effect:', {
      mapLoaded,
      hasMapInstance: !!mapInstanceRef.current,
      hasData: !!data,
      memberCount: data?.members?.length || 0,
      riskFilter
    });
    if (mapLoaded && mapInstanceRef.current && data) {
      updateMapMarkers();
    }
  }, [mapLoaded, data, selectedLayer, showHotspots, riskFilter]);

  const fetchGeographicRisk = async () => {
    const authToken = getAuthToken();

    if (!authToken) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const apiUrl = `${getWeaveinApiUrl()}/api/ml/predictions/geographic-risk/${cboId}`;
      console.log('[GeographicRiskMap] Fetching from:', apiUrl);
      
      const response = await authFetch(apiUrl);

      console.log('[GeographicRiskMap] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[GeographicRiskMap] API error:', errorText);
        throw new Error(`Failed to fetch geographic risk data: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[GeographicRiskMap] Data received:', {
        memberCount: result.members?.length || 0,
        hotspotCount: result.hotspots?.length || 0,
        totalMembers: result.total_members,
        hasMembers: !!result.members,
        sampleMember: result.members?.[0],
        fullResponse: result
      });
      
      if (!result.members || result.members.length === 0) {
        console.warn('[GeographicRiskMap] No members in response');
      }
      
      setData(result);
    } catch (err) {
      console.error('[GeographicRiskMap] Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load geographic risk data');
    } finally {
      setLoading(false);
    }
  };

  // Load Google Maps
  useEffect(() => {
    if (window.google && window.google.maps) {
      console.log('[GeographicRiskMap] Google Maps already available');
      setMapLoaded(true);
      // Don't call initializeMap here - let the useEffect handle it when all conditions are met
      return;
    }

    const script = document.createElement('script');
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      setError('Google Maps API key not configured');
      setLoading(false);
      return;
    }

    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=visualization`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('[GeographicRiskMap] Google Maps script loaded successfully');
      setMapLoaded(true);
      // Don't call initializeMap here - let the useEffect handle it when all conditions are met
    };
    script.onerror = (error) => {
      console.error('[GeographicRiskMap] Failed to load Google Maps script:', error);
      setError('Failed to load Google Maps');
      setLoading(false);
    };
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const initializeMap = () => {
    if (!mapRef.current || mapInstanceRef.current || !window.google?.maps) {
      console.log('Map initialization skipped:', {
        hasMapRef: !!mapRef.current,
        hasMapInstance: !!mapInstanceRef.current,
        hasGoogleMaps: !!window.google?.maps
      });
      return;
    }

    console.log('Initializing map...', { hasData: !!data, memberCount: data?.members?.length || 0 });

    // Default center to US center, or first member location if data is available
    let center = { lat: 39.8283, lng: -98.5795 };
    let zoom = 4;
    
    if (data?.members && data.members.length > 0) {
      const firstMember = data.members.find(m => m.latitude && m.longitude);
      if (firstMember) {
        center = { lat: firstMember.latitude, lng: firstMember.longitude };
        zoom = 10;
        console.log('Using member location for center:', center);
      }
    }

    try {
      // Detect if mobile/touch device
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center,
        zoom,
        mapTypeControl: !isTouchDevice, // Hide on mobile for more space
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_CENTER,
        },
        scaleControl: true,
        rotateControl: false,
        // 'greedy' allows single-finger pan on mobile (better UX)
        // 'cooperative' requires two-finger which can be frustrating
        gestureHandling: 'greedy',
      });

      console.log('Map initialized successfully');

      // Update markers if data is available
      if (data) {
        updateMapMarkers();
      }
    } catch (error) {
      console.error('Error initializing map:', error);
      setError('Failed to initialize map');
    }
  };

  const updateMapMarkers = () => {
    if (!mapInstanceRef.current || !data) {
      console.log('Cannot update markers:', {
        hasMapInstance: !!mapInstanceRef.current,
        hasData: !!data,
        memberCount: data?.members?.length || 0
      });
      return;
    }

    console.log('Updating map markers...', {
      totalMembers: data.members?.length || 0,
      riskFilter
    });

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    const filteredMembers = data.members.filter((member) => {
      if (riskFilter === 'all') return true;
      return member.risk_level === riskFilter;
    });

    console.log(`Filtered ${filteredMembers.length} members for display`);

    let markersAdded = 0;
    // Add member markers
    filteredMembers.forEach((member) => {
      if (!member.latitude || !member.longitude) {
        console.warn('Skipping member without coordinates:', member.member_id);
        return; // Skip members without coordinates
      }
      
      markersAdded++;
      
      const color = getRiskColor(member.risk_level, member.churn_probability);
      const marker = new window.google.maps.Marker({
        position: { lat: member.latitude, lng: member.longitude },
        map: mapInstanceRef.current,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: color,
          fillOpacity: 0.8,
          strokeColor: '#fff',
          strokeWeight: 2,
        },
        title: `${member.member_name || 'Member'} - ${member.risk_level}`,
      });

      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px;">
            <h3 style="margin: 0 0 8px 0; font-weight: 600;">${member.member_name}</h3>
            <p style="margin: 4px 0;"><strong>Risk Level:</strong> ${member.risk_level}</p>
            <p style="margin: 4px 0;"><strong>Churn Probability:</strong> ${(member.churn_probability * 100).toFixed(1)}%</p>
            <p style="margin: 4px 0;"><strong>Engagement Score:</strong> ${member.engagement_score.toFixed(1)}</p>
            <p style="margin: 4px 0;"><strong>ZIP Code:</strong> ${member.zip_code}</p>
          </div>
        `,
      });

      marker.addListener('click', () => {
        infoWindow.open(mapInstanceRef.current, marker);
        if (onMemberClick) {
          onMemberClick(member.member_id);
        }
      });

      markersRef.current.push(marker);
    });

    console.log(`Added ${markersAdded} markers to map`);

    // Add hotspot circles with enhanced SDOH visualization
    if (showHotspots) {
      data.hotspots.forEach((hotspot) => {
        // Determine color based on risk level and SDOH factors
        const riskScore = hotspot.avg_risk_score || 0;
        const sdohRisk = hotspot.sdoh_data?.combined_risk || riskScore * 100;

        // Color coding: green < 30, yellow 30-60, red > 60
        let strokeColor = '#16a34a'; // green
        let fillColor = '#16a34a';
        if (sdohRisk > 60 || riskScore > 0.6) {
          strokeColor = '#dc2626'; // red
          fillColor = '#dc2626';
        } else if (sdohRisk > 30 || riskScore > 0.3) {
          strokeColor = '#f97316'; // orange
          fillColor = '#f97316';
        }

        const circle = new window.google.maps.Circle({
          strokeColor,
          strokeOpacity: 0.9,
          strokeWeight: 3,
          fillColor,
          fillOpacity: 0.2,
          map: mapInstanceRef.current,
          center: { lat: hotspot.center_lat, lng: hotspot.center_lon },
          radius: hotspot.radius_miles * 1609.34, // Convert miles to meters
        });

        // Build enhanced SDOH info content
        const sdohSection = hotspot.sdoh_data ? `
          <div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #e5e7eb;">
            <h4 style="margin: 0 0 6px 0; font-size: 13px; color: #374151;">SDOH Risk Factors</h4>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 12px;">
              <div style="background: #eff6ff; padding: 4px 6px; border-radius: 4px;">
                <span style="color: #6b7280;">Transport:</span>
                <strong style="color: ${(hotspot.sdoh_data.transportation_barrier || 0) > 50 ? '#dc2626' : '#16a34a'};">
                  ${hotspot.sdoh_data.transportation_barrier?.toFixed(0) || 'N/A'}
                </strong>
              </div>
              <div style="background: #fef3c7; padding: 4px 6px; border-radius: 4px;">
                <span style="color: #6b7280;">Climate:</span>
                <strong style="color: ${(hotspot.sdoh_data.climate_risk || 0) > 50 ? '#dc2626' : '#16a34a'};">
                  ${hotspot.sdoh_data.climate_risk?.toFixed(0) || 'N/A'}
                </strong>
              </div>
              <div style="background: #fce7f3; padding: 4px 6px; border-radius: 4px;">
                <span style="color: #6b7280;">Housing:</span>
                <strong style="color: ${(hotspot.sdoh_data.housing_risk || 0) > 50 ? '#dc2626' : '#16a34a'};">
                  ${hotspot.sdoh_data.housing_risk?.toFixed(0) || 'N/A'}
                </strong>
              </div>
              <div style="background: #f3e8ff; padding: 4px 6px; border-radius: 4px;">
                <span style="color: #6b7280;">Combined:</span>
                <strong style="color: ${(hotspot.sdoh_data.combined_risk || 0) > 50 ? '#dc2626' : '#16a34a'};">
                  ${hotspot.sdoh_data.combined_risk?.toFixed(1) || 'N/A'}
                </strong>
              </div>
            </div>
          </div>
        ` : `
          <div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #e5e7eb;">
            <p style="font-size: 11px; color: #6b7280; font-style: italic;">
              SDOH data not loaded. Use ZIP lookup for detailed SDOH info.
            </p>
          </div>
        `;

        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 12px; max-width: 280px; font-family: system-ui, sans-serif;">
              <h3 style="margin: 0 0 8px 0; display: flex; align-items: center; gap: 6px; color: #1f2937;">
                <span style="width: 10px; height: 10px; border-radius: 50%; background: ${fillColor};"></span>
                Risk Hotspot
              </h3>
              <div style="display: grid; gap: 4px; font-size: 13px;">
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #6b7280;">Members:</span>
                  <strong>${hotspot.member_count}</strong>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #6b7280;">Avg Churn Risk:</span>
                  <strong style="color: ${riskScore > 0.5 ? '#dc2626' : riskScore > 0.3 ? '#f97316' : '#16a34a'};">
                    ${(riskScore * 100).toFixed(1)}%
                  </strong>
                </div>
                ${hotspot.zip_code ? `
                  <div style="display: flex; justify-content: space-between;">
                    <span style="color: #6b7280;">ZIP Code:</span>
                    <strong>${hotspot.zip_code}</strong>
                  </div>
                ` : ''}
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #6b7280;">Radius:</span>
                  <strong>${hotspot.radius_miles.toFixed(1)} mi</strong>
                </div>
              </div>
              ${hotspot.risk_factors && hotspot.risk_factors.length > 0 ? `
                <div style="margin-top: 8px;">
                  <span style="font-size: 12px; color: #6b7280;">Risk Factors:</span>
                  <div style="display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px;">
                    ${hotspot.risk_factors.map(f => `
                      <span style="font-size: 11px; background: #fee2e2; color: #dc2626; padding: 2px 6px; border-radius: 4px;">
                        ${f}
                      </span>
                    `).join('')}
                  </div>
                </div>
              ` : ''}
              ${sdohSection}
            </div>
          `,
        });

        circle.addListener('click', () => {
          infoWindow.setPosition({ lat: hotspot.center_lat, lng: hotspot.center_lon });
          infoWindow.open(mapInstanceRef.current);
        });

        // Store reference for potential SDOH overlay toggle
        markersRef.current.push(circle);
      });
    }
  };

  const getRiskColor = (riskLevel: string, probability: number): string => {
    if (probability >= 0.7 || riskLevel === 'critical' || riskLevel === 'lost') return '#DC2626'; // red
    if (probability >= 0.4 || riskLevel === 'at_risk') return '#F97316'; // orange
    if (probability >= 0.2 || riskLevel === 'watch') return '#EAB308'; // yellow
    return '#22C55E'; // green
  };

  // Fetch SDOH data for a ZIP code and show on map
  const handleZipCodeLookup = async () => {
    if (!zipCodeSearch || zipCodeSearch.length < 5) return;

    const authToken = getAuthToken();
    if (!authToken) {
      setSDOHError('Authentication required');
      return;
    }

    setLoadingSDOH(true);
    setSDOHError(null);
    setZipCodeSDOH(null);

    try {
      const response = await authFetch(`${getWeaveinApiUrl()}/api/sdoh-data/zip/${zipCodeSearch}/comprehensive`);

      if (response.ok) {
        const sdohData = await response.json();
        setZipCodeSDOH(sdohData);
        setShowSDOHPanel(true);

        // Add marker to map for this ZIP code
        if (mapInstanceRef.current && window.google?.maps) {
          // Try to geocode the ZIP code to get coordinates
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ address: zipCodeSearch + ', USA' }, (results: any, status: any) => {
            if (status === 'OK' && results[0]) {
              const location = results[0].geometry.location;

              // Clear previous SDOH marker
              if (sdohMarkerRef.current) {
                sdohMarkerRef.current.setMap(null);
              }
              if (sdohInfoWindowRef.current) {
                sdohInfoWindowRef.current.close();
              }

              // Determine marker color based on combined risk
              const riskScore = sdohData.combined_sdoh_risk_score || 50;
              const markerColor = riskScore > 70 ? '#DC2626' : riskScore > 40 ? '#F97316' : '#22C55E';

              // Create new marker
              sdohMarkerRef.current = new window.google.maps.Marker({
                position: location,
                map: mapInstanceRef.current,
                icon: {
                  path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
                  scale: 10,
                  fillColor: markerColor,
                  fillOpacity: 1,
                  strokeColor: '#fff',
                  strokeWeight: 2,
                },
                title: `SDOH Data: ZIP ${zipCodeSearch}`,
                zIndex: 1000,
              });

              // Create info window with SDOH summary
              const infoContent = `
                <div style="padding: 12px; max-width: 320px; font-family: system-ui, sans-serif;">
                  <h3 style="margin: 0 0 12px 0; font-weight: 700; font-size: 16px; color: #1f2937;">
                    📍 ZIP ${sdohData.zip_code} SDOH Profile
                  </h3>

                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 12px;">
                    <div style="background: #EFF6FF; padding: 8px; border-radius: 6px;">
                      <div style="font-size: 11px; color: #6b7280;">🚗 Transportation</div>
                      <div style="font-weight: 600; color: #1e40af;">
                        ${sdohData.transportation?.walk_score ?? 'N/A'} Walk
                      </div>
                    </div>
                    <div style="background: #F0FDF4; padding: 8px; border-radius: 6px;">
                      <div style="font-size: 11px; color: #6b7280;">🏠 Housing Risk</div>
                      <div style="font-weight: 600; color: ${(sdohData.housing?.risk_score || 0) > 50 ? '#dc2626' : '#16a34a'};">
                        ${sdohData.housing?.risk_score ?? 'N/A'}
                      </div>
                    </div>
                    <div style="background: #FEF3C7; padding: 8px; border-radius: 6px;">
                      <div style="font-size: 11px; color: #6b7280;">🌡️ Climate Risk</div>
                      <div style="font-weight: 600; color: ${(sdohData.climate?.climate_risk_score || 0) > 50 ? '#dc2626' : '#16a34a'};">
                        ${sdohData.climate?.climate_risk_score ?? 'N/A'}
                      </div>
                    </div>
                    <div style="background: #FEE2E2; padding: 8px; border-radius: 6px;">
                      <div style="font-size: 11px; color: #6b7280;">📊 Combined Risk</div>
                      <div style="font-weight: 700; font-size: 18px; color: ${riskScore > 70 ? '#dc2626' : riskScore > 40 ? '#f97316' : '#16a34a'};">
                        ${riskScore.toFixed(1)}
                      </div>
                    </div>
                  </div>

                  <div style="font-size: 12px; color: #6b7280; border-top: 1px solid #e5e7eb; padding-top: 8px;">
                    <div>🏥 Flood: <strong>${sdohData.climate?.flood_risk_level || 'N/A'}</strong></div>
                    <div>🔥 Wildfire: <strong>${sdohData.climate?.wildfire_risk_level || 'N/A'}</strong></div>
                    <div>💰 Cost Burdened: <strong>${sdohData.housing?.pct_cost_burdened ? sdohData.housing.pct_cost_burdened.toFixed(1) + '%' : 'N/A'}</strong></div>
                  </div>
                </div>
              `;

              sdohInfoWindowRef.current = new window.google.maps.InfoWindow({
                content: infoContent,
              });

              // Open info window
              sdohInfoWindowRef.current.open(mapInstanceRef.current, sdohMarkerRef.current);

              // Pan to location
              mapInstanceRef.current.panTo(location);
              mapInstanceRef.current.setZoom(12);

              // Click handler to reopen info window
              sdohMarkerRef.current.addListener('click', () => {
                sdohInfoWindowRef.current.open(mapInstanceRef.current, sdohMarkerRef.current);
              });
            }
          });
        }
      } else {
        setSDOHError('No SDOH data found for this ZIP code');
        // Still show the panel with N/A values
        setZipCodeSDOH({
          zip_code: zipCodeSearch,
          combined_sdoh_risk_score: undefined,
        });
        setShowSDOHPanel(true);
      }
    } catch (err) {
      console.error('Error fetching SDOH data:', err);
      setSDOHError('Failed to fetch SDOH data');
    } finally {
      setLoadingSDOH(false);
    }
  };

  const clearSDOHLookup = () => {
    setZipCodeSearch('');
    setZipCodeSDOH(null);
    setShowSDOHPanel(false);
    setSDOHError(null);
    if (sdohMarkerRef.current) {
      sdohMarkerRef.current.setMap(null);
      sdohMarkerRef.current = null;
    }
    if (sdohInfoWindowRef.current) {
      sdohInfoWindowRef.current.close();
      sdohInfoWindowRef.current = null;
    }
  };

  // Location search handler (city, state, or address)
  const handleLocationSearch = async () => {
    if (!locationSearch.trim() || !mapInstanceRef.current || !window.google?.maps) {
      return;
    }

    setLocationLoading(true);
    setLocationError(null);

    try {
      const geocoder = new window.google.maps.Geocoder();

      const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        geocoder.geocode(
          { address: locationSearch, componentRestrictions: { country: 'US' } },
          (results, status) => {
            if (status === 'OK' && results && results.length > 0) {
              resolve(results);
            } else {
              reject(new Error(`Geocoding failed: ${status}`));
            }
          }
        );
      });

      if (result && result.length > 0) {
        const location = result[0].geometry.location;
        const formattedAddress = result[0].formatted_address;

        // Clear previous location marker
        if (locationMarkerRef.current) {
          locationMarkerRef.current.setMap(null);
        }

        // Pan to location and zoom appropriately
        mapInstanceRef.current.panTo(location);

        // Adjust zoom based on result type
        const types = result[0].types;
        let zoomLevel = 12; // Default for city
        if (types.includes('postal_code')) {
          zoomLevel = 14;
        } else if (types.includes('locality') || types.includes('administrative_area_level_2')) {
          zoomLevel = 11;
        } else if (types.includes('administrative_area_level_1')) {
          zoomLevel = 7;
        } else if (types.includes('street_address') || types.includes('route')) {
          zoomLevel = 15;
        }
        mapInstanceRef.current.setZoom(zoomLevel);

        // Add a marker for the searched location
        locationMarkerRef.current = new window.google.maps.Marker({
          position: location,
          map: mapInstanceRef.current,
          title: formattedAddress,
          icon: {
            path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
            scale: 6,
            fillColor: '#4F46E5',
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 2,
          },
          animation: window.google.maps.Animation.DROP,
        });

        // Show info window
        const infoWindow = new window.google.maps.InfoWindow({
          content: `<div style="padding: 8px; max-width: 200px;"><strong>${formattedAddress}</strong></div>`,
        });
        infoWindow.open(mapInstanceRef.current, locationMarkerRef.current);

        // Auto-close info window after 5 seconds
        setTimeout(() => infoWindow.close(), 5000);
      }
    } catch (err) {
      console.error('Location search error:', err);
      setLocationError('Location not found. Try city, state or ZIP code.');
    } finally {
      setLocationLoading(false);
    }
  };

  const clearLocationSearch = () => {
    setLocationSearch('');
    setLocationError(null);
    if (locationMarkerRef.current) {
      locationMarkerRef.current.setMap(null);
      locationMarkerRef.current = null;
    }
  };

  // Zoom controls
  const handleZoomIn = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setZoom(mapInstanceRef.current.getZoom() + 1);
    }
  };

  const handleZoomOut = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setZoom(mapInstanceRef.current.getZoom() - 1);
    }
  };

  const handleResetView = () => {
    if (mapInstanceRef.current && data?.members && data.members.length > 0) {
      // Find bounds that include all members
      const bounds = new window.google.maps.LatLngBounds();
      data.members.forEach(member => {
        if (member.latitude && member.longitude) {
          bounds.extend({ lat: member.latitude, lng: member.longitude });
        }
      });
      mapInstanceRef.current.fitBounds(bounds);
    } else if (mapInstanceRef.current) {
      // Reset to US center
      mapInstanceRef.current.setCenter({ lat: 39.8283, lng: -98.5795 });
      mapInstanceRef.current.setZoom(4);
    }
  };

  // Current location handler using Geolocation API
  const handleCurrentLocation = () => {
    console.log('[GeographicRiskMap] handleCurrentLocation called');

    // Check if running on HTTPS (required for geolocation)
    if (typeof window !== 'undefined' && window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      console.warn('[GeographicRiskMap] Geolocation requires HTTPS');
      setCurrentLocationError('Location requires secure connection (HTTPS)');
      return;
    }

    if (!navigator.geolocation) {
      console.error('[GeographicRiskMap] Geolocation not supported');
      setCurrentLocationError('Geolocation is not supported by your browser');
      return;
    }

    if (!mapInstanceRef.current || !window.google?.maps) {
      console.error('[GeographicRiskMap] Map not ready for geolocation');
      setCurrentLocationError('Map not ready. Please wait and try again.');
      return;
    }

    setCurrentLocationLoading(true);
    setCurrentLocationError(null);
    console.log('[GeographicRiskMap] Requesting geolocation...');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('[GeographicRiskMap] Geolocation success:', position.coords);
        const { latitude, longitude } = position.coords;
        const location = { lat: latitude, lng: longitude };

        // Clear previous current location marker and its accuracy circle
        if (currentLocationMarkerRef.current) {
          if ((currentLocationMarkerRef.current as any).accuracyCircle) {
            (currentLocationMarkerRef.current as any).accuracyCircle.setMap(null);
          }
          currentLocationMarkerRef.current.setMap(null);
        }

        // Create a blue dot marker for current location
        currentLocationMarkerRef.current = new window.google.maps.Marker({
          position: location,
          map: mapInstanceRef.current,
          title: 'Your Current Location',
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: '#4285F4',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3,
          },
          zIndex: 1001,
        });

        // Add a semi-transparent accuracy circle
        const accuracyCircle = new window.google.maps.Circle({
          strokeColor: '#4285F4',
          strokeOpacity: 0.4,
          strokeWeight: 2,
          fillColor: '#4285F4',
          fillOpacity: 0.15,
          map: mapInstanceRef.current,
          center: location,
          radius: Math.max(position.coords.accuracy, 50), // Minimum 50m for visibility
        });

        // Store accuracy circle reference for cleanup
        (currentLocationMarkerRef.current as any).accuracyCircle = accuracyCircle;

        // Pan to current location
        mapInstanceRef.current.panTo(location);
        mapInstanceRef.current.setZoom(14);

        // Show info window
        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 10px; font-family: system-ui, sans-serif;">
              <strong style="color: #4285F4; font-size: 14px;">📍 Your Location</strong>
              <p style="margin: 6px 0 0 0; font-size: 12px; color: #666;">
                Accuracy: ±${Math.round(position.coords.accuracy)}m
              </p>
            </div>
          `,
        });
        infoWindow.open(mapInstanceRef.current, currentLocationMarkerRef.current);
        setTimeout(() => infoWindow.close(), 5000);

        setCurrentLocationLoading(false);
      },
      (error) => {
        console.error('[GeographicRiskMap] Geolocation error:', error.code, error.message);
        setCurrentLocationLoading(false);

        let errorMessage: string;
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please allow location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location unavailable. Please check your device settings.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
          default:
            errorMessage = 'Could not get your location. Please try again.';
        }
        setCurrentLocationError(errorMessage);

        // Auto-clear error after 8 seconds
        setTimeout(() => setCurrentLocationError(null), 8000);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000, // Increased timeout for slower connections
        maximumAge: 30000, // Cache for 30 seconds
      }
    );
  };

  const handleExportMap = () => {
    // Export map as image
    if (mapInstanceRef.current) {
      const mapDiv = mapRef.current;
      if (mapDiv) {
        // Simple export - could use html2canvas for better results
        window.print();
      }
    }
  };

  // Geocode all members for this CBO
  const handleGeocodeMembers = async () => {
    const authToken = getAuthToken();
    if (!authToken) {
      setGeocodingResult({ success: false, message: 'Authentication required' });
      return;
    }

    setGeocodingInProgress(true);
    setGeocodingResult(null);

    try {
      // First, check geocoding status to see if there are members needing geocoding
      const statusUrl = `${getWeaveinApiUrl()}/api/ml/geocode/status/${cboId}`;
      console.log('[GeographicRiskMap] Checking geocoding status:', statusUrl);

      const statusResponse = await authFetch(statusUrl, {
        method: 'GET'
      });

      if (statusResponse.ok) {
        const status = await statusResponse.json();
        console.log('[GeographicRiskMap] Geocoding status:', status);

        // If all members are already geocoded, show that info
        if (status.needs_geocoding === 0) {
          let message = `All ${status.fully_geocoded} members are already geocoded.`;
          if (status.total_members > status.has_address_data) {
            message += ` ${status.total_members - status.has_address_data} members have no address data.`;
          }
          setGeocodingResult({
            success: true,
            message,
            geocoded_count: 0,
            failed_count: 0,
          });
          setGeocodingInProgress(false);
          return;
        }
      }

      // Proceed with geocoding
      const apiUrl = `${getWeaveinApiUrl()}/api/ml/geocode/members/${cboId}`;
      console.log('[GeographicRiskMap] Starting geocoding:', apiUrl);

      const response = await authFetch(apiUrl, {
        method: 'POST'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[GeographicRiskMap] Geocoding API error:', errorText);
        setGeocodingResult({
          success: false,
          message: `Geocoding failed: ${response.status} ${response.statusText}`,
        });
        return;
      }

      const result = await response.json();
      console.log('[GeographicRiskMap] Geocoding result:', result);

      // Extract geocoding stats from nested geocoding_results object
      const geocodingStats = result.geocoding_results || result;
      const geocodedCount = geocodingStats.successful ?? geocodingStats.geocoded_count ?? geocodingStats.total_geocoded ?? 0;
      const failedCount = geocodingStats.failed ?? geocodingStats.failed_count ?? geocodingStats.total_failed ?? 0;
      const totalCount = geocodingStats.total ?? (geocodedCount + failedCount);

      // Build appropriate message
      let message = result.message || 'Geocoding completed';
      if (totalCount === 0) {
        message = 'No members found needing geocoding. Members may already be geocoded or may not have address data.';
      }

      setGeocodingResult({
        success: true,
        message,
        geocoded_count: geocodedCount,
        failed_count: failedCount,
      });

      // Refresh the map data after geocoding
      if (geocodedCount > 0) {
        await fetchGeographicRisk();
      }
    } catch (err) {
      console.error('[GeographicRiskMap] Geocoding error:', err);
      setGeocodingResult({
        success: false,
        message: err instanceof Error ? err.message : 'Geocoding failed',
      });
    } finally {
      setGeocodingInProgress(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-96">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <p className="text-destructive">{error}</p>
            <Button onClick={fetchGeographicRisk} className="mt-4">
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show helpful message if no geocoded members
  if (data && data.members.length === 0) {
    return (
      <Card>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-96 text-center">
            <MapPin className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Geocoded Members Found</h3>
            <p className="text-muted-foreground max-w-md mb-4">
              Members need to be geocoded before they can be displayed on the map.
              This process converts member addresses into map coordinates.
            </p>
            <div className="space-y-2 text-sm text-muted-foreground mb-4">
              <p>To populate the map:</p>
              <ul className="list-disc list-inside text-left">
                <li>Ensure members have valid address data (street, city, state, ZIP)</li>
                <li>Click the "Geocode Members" button below to start the geocoding process</li>
              </ul>
            </div>

            {/* Geocoding Result Alert */}
            {geocodingResult && (
              <div
                className={`mb-4 p-3 rounded-lg text-sm max-w-md ${
                  geocodingResult.success
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}
              >
                <p className="font-medium">{geocodingResult.message}</p>
                {geocodingResult.success && (
                  <p className="mt-1">
                    Geocoded: {geocodingResult.geocoded_count || 0} members
                    {geocodingResult.failed_count ? `, Failed: ${geocodingResult.failed_count}` : ''}
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={handleGeocodeMembers}
                disabled={geocodingInProgress}
                className="bg-v2accent hover:bg-v2accent/90"
              >
                {geocodingInProgress ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Geocoding...
                  </>
                ) : (
                  <>
                    <MapPin className="h-4 w-4 mr-2" />
                    Geocode Members
                  </>
                )}
              </Button>
              <Button onClick={fetchGeographicRisk} variant="outline" disabled={geocodingInProgress}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {/* Toolbar (page title rendered by the shell chrome) */}
      <CardHeader>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={handleGeocodeMembers}
              disabled={geocodingInProgress}
              title="Geocode member addresses to show them on the map"
            >
              {geocodingInProgress ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <MapPin className="h-4 w-4 mr-2" />
              )}
              {geocodingInProgress ? 'Geocoding...' : 'Geocode Members'}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchGeographicRisk} disabled={geocodingInProgress}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportMap}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
        {/* Geocoding Result Banner */}
        {geocodingResult && (
          <div
            className={`mt-3 p-3 rounded-lg text-sm flex items-center justify-between ${
              geocodingResult.success
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            <div>
              <span className="font-medium">{geocodingResult.message}</span>
              {geocodingResult.success && geocodingResult.geocoded_count !== undefined && (
                <span className="ml-2">
                  ({geocodingResult.geocoded_count} geocoded
                  {geocodingResult.failed_count ? `, ${geocodingResult.failed_count} failed` : ''})
                </span>
              )}
            </div>
            <button
              onClick={() => setGeocodingResult(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Filters - Mobile Responsive */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
            {/* Risk Filter */}
            <div className="flex items-center gap-2">
              <Label htmlFor="risk-filter" className="whitespace-nowrap text-sm">Risk:</Label>
              <Select value={riskFilter} onValueChange={setRiskFilter}>
                <SelectTrigger id="risk-filter" className="w-32 sm:w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="at_risk">At Risk</SelectItem>
                  <SelectItem value="watch">Watch</SelectItem>
                  <SelectItem value="healthy">Healthy</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Checkboxes Row */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-hotspots"
                  checked={showHotspots}
                  onCheckedChange={(checked) => setShowHotspots(checked === true)}
                />
                <Label htmlFor="show-hotspots" className="cursor-pointer text-sm">
                  Hotspots
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-sdoh"
                  checked={showSDOH}
                  onCheckedChange={(checked) => setShowSDOH(checked === true)}
                />
                <Label htmlFor="show-sdoh" className="cursor-pointer text-sm">
                  SDOH Layers
                </Label>
              </div>
            </div>

            {/* ZIP Code SDOH Lookup */}
            <div className="flex items-center gap-2 sm:ml-auto">
              <div className="relative flex-1 sm:flex-none">
                <input
                  type="text"
                  placeholder="ZIP Lookup"
                  className="w-full sm:w-32 pl-3 pr-8 py-1.5 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={zipCodeSearch}
                  onChange={(e) => setZipCodeSearch(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  maxLength={5}
                  onKeyDown={(e) => e.key === 'Enter' && handleZipCodeLookup()}
                />
                {zipCodeSearch && (
                  <button
                    onClick={clearSDOHLookup}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <Button
                size="sm"
                onClick={handleZipCodeLookup}
                disabled={loadingSDOH || zipCodeSearch.length < 5}
                className="flex-shrink-0"
              >
                {loadingSDOH ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Navigation Controls Row - Mobile Responsive */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-stretch sm:items-center bg-gray-50 dark:bg-gray-800 p-2 rounded-lg">
            {/* Location Search */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Navigation className="h-4 w-4 text-gray-500 flex-shrink-0 hidden sm:block" />
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search city, state..."
                  className="w-full pl-3 pr-8 py-1.5 border rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={locationSearch}
                  onChange={(e) => setLocationSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLocationSearch()}
                />
                {locationSearch && (
                  <button
                    onClick={clearLocationSearch}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleLocationSearch}
                disabled={locationLoading || !locationSearch.trim()}
                className="flex-shrink-0"
              >
                {locationLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
              {locationError && (
                <span className="text-xs text-red-500 hidden sm:inline">{locationError}</span>
              )}
            </div>

            {/* Zoom & Location Controls */}
            <div className="flex items-center gap-1 sm:ml-auto sm:border-l sm:pl-4">
              <span className="text-xs text-gray-500 mr-2 hidden sm:inline">Zoom:</span>
              <Button
                size="sm"
                variant="outline"
                onClick={handleZoomIn}
                className="h-8 w-8 p-0"
                title="Zoom In"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleZoomOut}
                className="h-8 w-8 p-0"
                title="Zoom Out"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCurrentLocation}
                disabled={currentLocationLoading}
                className="h-8 w-8 p-0"
                title="Go to my current location"
              >
                {currentLocationLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Locate className="h-4 w-4" />
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleResetView}
                className="h-8 px-2"
                title="Reset to show all members"
              >
                <MapPin className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Reset View</span>
              </Button>
            </div>
          </div>

          {/* Location Error Alert */}
          {currentLocationError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{currentLocationError}</span>
              <button
                onClick={() => setCurrentLocationError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* SDOH Data Panel */}
          {showSDOHPanel && zipCodeSDOH && (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-blue-600" />
                  SDOH Profile: ZIP {zipCodeSDOH.zip_code}
                </h4>
                <button onClick={clearSDOHLookup} className="text-gray-400 hover:text-gray-600">
                  <X className="h-4 w-4" />
                </button>
              </div>

              {sdohError && (
                <p className="text-sm text-amber-600 mb-2">{sdohError}</p>
              )}

              {/* Data seeded indicator */}
              {zipCodeSDOH.data_seeded && (
                <div className="mb-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  Data auto-generated for this ZIP code
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* Transportation */}
                <div className="bg-white p-3 rounded-lg border shadow-sm">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Car className="h-4 w-4 text-blue-500" />
                    <span className="text-xs font-medium text-gray-600">Transportation</span>
                  </div>
                  <div className="text-lg font-bold text-blue-700">
                    {zipCodeSDOH.transportation?.walk_score ?? 'N/A'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {zipCodeSDOH.transportation?.walk_score_description || 'Walk Score'}
                  </div>
                  {zipCodeSDOH.transportation?.transit_score && (
                    <div className="text-xs text-blue-500 mt-1">
                      Transit: {zipCodeSDOH.transportation.transit_score}
                    </div>
                  )}
                </div>

                {/* Climate */}
                <div className="bg-white p-3 rounded-lg border shadow-sm">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Cloud className="h-4 w-4 text-sky-500" />
                    <span className="text-xs font-medium text-gray-600">Climate Risk</span>
                  </div>
                  <div className={`text-lg font-bold ${
                    (zipCodeSDOH.climate?.climate_risk_score ?? 0) > 70 ? 'text-red-600' :
                    (zipCodeSDOH.climate?.climate_risk_score ?? 0) > 40 ? 'text-amber-600' : 'text-green-600'
                  }`}>
                    {zipCodeSDOH.climate?.climate_risk_score?.toFixed(1) ?? 'N/A'}
                  </div>
                  <div className="text-xs text-gray-500">Risk Score</div>
                  {zipCodeSDOH.climate?.wildfire_risk_level && (
                    <div className="text-xs text-amber-600 mt-1">
                      Fire: {zipCodeSDOH.climate.wildfire_risk_level}
                    </div>
                  )}
                </div>

                {/* Housing */}
                <div className="bg-white p-3 rounded-lg border shadow-sm">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Home className="h-4 w-4 text-amber-500" />
                    <span className="text-xs font-medium text-gray-600">Housing Risk</span>
                  </div>
                  <div className={`text-lg font-bold ${
                    (zipCodeSDOH.housing?.risk_score ?? 0) > 70 ? 'text-red-600' :
                    (zipCodeSDOH.housing?.risk_score ?? 0) > 40 ? 'text-amber-600' : 'text-green-600'
                  }`}>
                    {zipCodeSDOH.housing?.risk_score?.toFixed(1) ?? 'N/A'}
                  </div>
                  <div className="text-xs text-gray-500">Risk Score</div>
                  {zipCodeSDOH.housing?.median_gross_rent && (
                    <div className="text-xs text-gray-600 mt-1">
                      Rent: ${zipCodeSDOH.housing.median_gross_rent.toLocaleString()}
                    </div>
                  )}
                </div>

                {/* Combined */}
                <div className="bg-white p-3 rounded-lg border shadow-sm">
                  <div className="flex items-center gap-1.5 mb-1">
                    <TrendingUp className="h-4 w-4 text-purple-500" />
                    <span className="text-xs font-medium text-gray-600">Combined SDOH</span>
                  </div>
                  <div className={`text-xl font-bold ${
                    (zipCodeSDOH.combined_sdoh_risk_score ?? 0) > 70 ? 'text-red-600' :
                    (zipCodeSDOH.combined_sdoh_risk_score ?? 0) > 40 ? 'text-amber-600' : 'text-green-600'
                  }`}>
                    {zipCodeSDOH.combined_sdoh_risk_score?.toFixed(1) ?? 'N/A'}
                  </div>
                  <div className="text-xs text-gray-500">Risk Score</div>
                </div>
              </div>

              {/* Additional details row */}
              <div className="mt-3 grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
                <div className="bg-white px-2 py-1.5 rounded border">
                  <span className="text-gray-500">Flood:</span>{' '}
                  <span className={`font-medium ${
                    zipCodeSDOH.climate?.flood_risk_level === 'high' ? 'text-red-600' : 'text-green-600'
                  }`}>{zipCodeSDOH.climate?.flood_risk_level ?? 'N/A'}</span>
                </div>
                <div className="bg-white px-2 py-1.5 rounded border">
                  <span className="text-gray-500">Wildfire:</span>{' '}
                  <span className={`font-medium ${
                    zipCodeSDOH.climate?.wildfire_risk_level === 'high' || zipCodeSDOH.climate?.wildfire_risk_level === 'very_high' ? 'text-red-600' : 'text-green-600'
                  }`}>{zipCodeSDOH.climate?.wildfire_risk_level ?? 'N/A'}</span>
                </div>
                <div className="bg-white px-2 py-1.5 rounded border">
                  <span className="text-gray-500">AQI:</span>{' '}
                  <span className={`font-medium ${
                    (zipCodeSDOH.climate?.aqi_annual_median ?? 0) > 100 ? 'text-red-600' :
                    (zipCodeSDOH.climate?.aqi_annual_median ?? 0) > 50 ? 'text-amber-600' : 'text-green-600'
                  }`}>{zipCodeSDOH.climate?.aqi_annual_median ?? 'N/A'}</span>
                </div>
                <div className="bg-white px-2 py-1.5 rounded border">
                  <span className="text-gray-500">Eviction:</span>{' '}
                  <span className={`font-medium ${
                    (zipCodeSDOH.housing?.eviction_rate ?? 0) > 3 ? 'text-red-600' : 'text-green-600'
                  }`}>{zipCodeSDOH.housing?.eviction_rate ? `${zipCodeSDOH.housing.eviction_rate.toFixed(1)}%` : 'N/A'}</span>
                </div>
                <div className="bg-white px-2 py-1.5 rounded border">
                  <span className="text-gray-500">Cost Burden:</span>{' '}
                  <span className={`font-medium ${
                    (zipCodeSDOH.housing?.pct_cost_burdened ?? 0) > 50 ? 'text-red-600' : 'text-green-600'
                  }`}>{zipCodeSDOH.housing?.pct_cost_burdened ? `${zipCodeSDOH.housing.pct_cost_burdened.toFixed(1)}%` : 'N/A'}</span>
                </div>
                <div className="bg-white px-2 py-1.5 rounded border">
                  <span className="text-gray-500">Transit:</span>{' '}
                  <span className={`font-medium ${
                    (zipCodeSDOH.transportation?.transit_score ?? 0) >= 50 ? 'text-green-600' : 'text-amber-600'
                  }`}>{zipCodeSDOH.transportation?.transit_score ?? 'N/A'}</span>
                </div>
              </div>

              {/* Extended details - collapsible */}
              <details className="mt-3">
                <summary className="text-xs text-gray-600 cursor-pointer hover:text-blue-600">
                  View more SDOH details...
                </summary>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs bg-gray-50 p-2 rounded">
                  <div className="bg-white px-2 py-1.5 rounded border">
                    <span className="text-gray-500">No Vehicle HH:</span>{' '}
                    <span className="font-medium">{zipCodeSDOH.transportation?.pct_no_vehicle ? `${zipCodeSDOH.transportation.pct_no_vehicle.toFixed(1)}%` : 'N/A'}</span>
                  </div>
                  <div className="bg-white px-2 py-1.5 rounded border">
                    <span className="text-gray-500">Commute Time:</span>{' '}
                    <span className="font-medium">{zipCodeSDOH.transportation?.mean_commute_time ? `${zipCodeSDOH.transportation.mean_commute_time.toFixed(0)} min` : 'N/A'}</span>
                  </div>
                  <div className="bg-white px-2 py-1.5 rounded border">
                    <span className="text-gray-500">Homeless/10k:</span>{' '}
                    <span className="font-medium">{zipCodeSDOH.housing?.homeless_per_10k?.toFixed(1) ?? 'N/A'}</span>
                  </div>
                  <div className="bg-white px-2 py-1.5 rounded border">
                    <span className="text-gray-500">Owner Occupied:</span>{' '}
                    <span className="font-medium">{zipCodeSDOH.housing?.pct_owner_occupied ? `${zipCodeSDOH.housing.pct_owner_occupied.toFixed(1)}%` : 'N/A'}</span>
                  </div>
                  <div className="bg-white px-2 py-1.5 rounded border">
                    <span className="text-gray-500">Pollutant:</span>{' '}
                    <span className="font-medium">{zipCodeSDOH.climate?.primary_pollutant ?? 'N/A'}</span>
                  </div>
                  <div className="bg-white px-2 py-1.5 rounded border">
                    <span className="text-gray-500">Good AQI Days:</span>{' '}
                    <span className="font-medium">{zipCodeSDOH.climate?.aqi_days_good ?? 'N/A'}</span>
                  </div>
                  <div className="bg-white px-2 py-1.5 rounded border">
                    <span className="text-gray-500">Severe Cost Burden:</span>{' '}
                    <span className="font-medium">{zipCodeSDOH.housing?.pct_severely_cost_burdened ? `${zipCodeSDOH.housing.pct_severely_cost_burdened.toFixed(1)}%` : 'N/A'}</span>
                  </div>
                  <div className="bg-white px-2 py-1.5 rounded border">
                    <span className="text-gray-500">Stability Score:</span>{' '}
                    <span className="font-medium">{zipCodeSDOH.housing?.stability_score?.toFixed(1) ?? 'N/A'}</span>
                  </div>
                </div>
              </details>
            </div>
          )}

          {/* Map - Responsive Height */}
          <div ref={mapRef} className="w-full h-[350px] sm:h-[450px] md:h-[600px] rounded-lg border touch-pan-y" />

          {/* Legend */}
          <div className="flex flex-wrap gap-4 p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-[#DC2626]" />
              <span className="text-sm">Critical/Lost (70%+)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-[#F97316]" />
              <span className="text-sm">At Risk (40-70%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-[#EAB308]" />
              <span className="text-sm">Watch (20-40%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-[#22C55E]" />
              <span className="text-sm">Healthy (&lt;20%)</span>
            </div>
          </div>

          {/* Summary Stats */}
          {data && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Total Members</p>
                <p className="text-2xl font-bold">{data.members.length}</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Hotspots</p>
                <p className="text-2xl font-bold">{data.hotspots.length}</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">High Risk</p>
                <p className="text-2xl font-bold text-destructive">
                  {data.members.filter((m) => m.churn_probability >= 0.4).length}
                </p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Avg Risk</p>
                <p className="text-2xl font-bold">
                  {(
                    data.members.reduce((sum, m) => sum + m.churn_probability, 0) /
                    data.members.length
                  ).toFixed(1)}
                </p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default GeographicRiskMap;

